import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';

interface StyleInfo {
  id: string;
  label: string;
  thumbnailUrl: string;
}

@Component({
  selector: 'app-poster-generator',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './poster-generator.component.html',
  styleUrl: './poster-generator.component.scss',
})
export class PosterGeneratorComponent implements OnInit {
  selectedStyle: string = '';
  styles: StyleInfo[] = [];
  
  isGenerating = false;
  posterUrl: string | null = null;
  posterBlob: Blob | null = null;
  errorMessage: string = '';

  nextEvent!: { dateString: string; dayString: string };
  pastorName: string = 'Gennaro Chiocca';

  ngOnInit() {
    this.nextEvent = this.getNextEventInfo();
    this.setAvailableStyles();
  }

  setAvailableStyles() {
    const today = new Date().getDate();
    if (today >= 1 && today <= 7) {
      this.styles = [
        { id: 'style-4', label: 'Stile 4 (Speciale)', thumbnailUrl: '/assets/templates/style-4/template.png' },
      ];
    } else {
      this.styles = [
        { id: 'style-1', label: 'Stile 1 (Classico)', thumbnailUrl: '/assets/templates/poster-1/template.png' },
        { id: 'style-2', label: 'Stile 2 (Nuovo)', thumbnailUrl: '/assets/templates/style-2/template.png' },
        { id: 'style-3', label: 'Stile 3', thumbnailUrl: '/assets/templates/style-3/template.png' },
      ];
    }
  }

  selectStyle(id: string): void {
    this.selectedStyle = id;
  }

  getNextEventInfo(): { dateString: string; dayString: string } {
    const now = new Date();
    const currentDay = now.getDay();
    let daysToAdd = 0;

    if (currentDay === 1) daysToAdd = 1;
    else if (currentDay === 2) daysToAdd = 0;
    else if (currentDay === 3) daysToAdd = 1;
    else if (currentDay === 4) daysToAdd = 0;
    else if (currentDay === 5) daysToAdd = 2;
    else if (currentDay === 6) daysToAdd = 1;
    else if (currentDay === 0) daysToAdd = 0;

    const nextDate = new Date(now);
    nextDate.setDate(now.getDate() + daysToAdd);

    const formatter = new Intl.DateTimeFormat('it-IT', { weekday: 'long', day: 'numeric', month: 'long' });
    
    const dayOfWeek = nextDate.getDay();
    let dayString = 'tue';
    if (dayOfWeek === 4) dayString = 'thu';
    if (dayOfWeek === 0) dayString = 'sun';

    return {
      dateString: formatter.format(nextDate).toUpperCase(),
      dayString: dayString
    };
  }

  async generatePoster(): Promise<void> {
    if (!this.selectedStyle) return;

    this.isGenerating = true;
    this.posterUrl = null;
    this.errorMessage = '';

    let line1 = this.nextEvent.dateString;
    
    // Style 3 usa il formato DD.MM.YYYY
    if (this.selectedStyle === 'style-3') {
      const now = new Date();
      let daysToAdd = 0;
      const currentDay = now.getDay();
      if (currentDay === 1) daysToAdd = 1;
      else if (currentDay === 2) daysToAdd = 0;
      else if (currentDay === 3) daysToAdd = 1;
      else if (currentDay === 4) daysToAdd = 0;
      else if (currentDay === 5) daysToAdd = 2;
      else if (currentDay === 6) daysToAdd = 1;
      else if (currentDay === 0) daysToAdd = 0;
      
      const nextDate = new Date(now);
      nextDate.setDate(now.getDate() + daysToAdd);
      
      const dd = String(nextDate.getDate()).padStart(2, '0');
      const mm = String(nextDate.getMonth() + 1).padStart(2, '0');
      const yyyy = nextDate.getFullYear();
      line1 = `${dd}.${mm}.${yyyy}`;
    }

    const line2 = "H 20:00";
    const pastor = this.nextEvent.dayString === 'sun' ? this.pastorName : '';

    try {
      const response = await fetch('/api/generate-poster', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          styleId: this.selectedStyle,
          day: this.nextEvent.dayString,
          line1: line1,
          line2: line2,
          pastor: pastor
        }),
      });

      if (!response.ok) {
        throw new Error('Errore nella generazione della locandina');
      }

      const blob = await response.blob();
      this.posterBlob = blob;
      this.posterUrl = URL.createObjectURL(blob);
    } catch {
      this.errorMessage = 'Si è verificato un errore. Riprova più tardi.';
    } finally {
      this.isGenerating = false;
    }
  }

  downloadPoster(): void {
    if (!this.posterUrl) return;

    const a = document.createElement('a');
    a.href = this.posterUrl;
    a.download = `locandina-${Date.now()}.png`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  }

  async sharePoster(): Promise<void> {
    if (!this.posterBlob) return;

    const file = new File([this.posterBlob], `locandina-${Date.now()}.png`, { type: this.posterBlob.type });

    if (navigator.canShare && navigator.canShare({ files: [file] })) {
      try {
        await navigator.share({
          files: [file],
          title: 'Locandina',
        });
      } catch (error) {
        console.error('Errore durante la condivisione:', error);
      }
    } else {
      // Fallback: se la condivisione non è supportata, procediamo con il download
      this.downloadPoster();
    }
  }
}
